//@ts-nocheck
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Carousel } from '@mantine/carousel';
import { Box, Flex, Pagination, ScrollArea, Table, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { AppRoutes } from '@/router/routes.ts';

import { EditBooking } from '@/features/EditBooking/EditBooking';

import { bookingApi } from '@/shared/api/booking.ts';
import {
	chunk,
	isActiveBooking,
	isActiveGuest,
	isEndedBooking,
	isFutureBooking,
	isNearestBooking,
} from '@/shared/helpers/utils';
import useAppStore from '@/shared/store/app.store';
import { PersonBookingsObject } from '@/shared/types/booking.types';
import IconChevronRight from '@/shared/uikit/assets/icons/IconChevronRight.svg?react';
import IconDelete from '@/shared/uikit/assets/icons/IconDelete.svg?react';
import IconEdit from '@/shared/uikit/assets/icons/IconEdit.svg?react';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { CopyButton } from '@/shared/uikit/components/button/CopyButton';
import { SliderButton } from '@/shared/uikit/components/button/sliderButton';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { ActiveBookingActions } from './ActiveBookingActions';
import { ActiveBookingSelected } from './ActiveBookingSelected';

const buttonsSlides = ['Все', 'Активные', 'Будущие', 'Завершенные'];

export const ActiveBookingMore = () => {
	const navigate = useNavigate();
	const isMobile = useAppStore((state) => state.isMobile);
	const [activePage, setActivePage] = useState(1);
	const [selectedWorkSpace, setSelectedWorkSpace] = useState<PersonBookingsObject>();
	const [currentTab, setCurrentTab] = useState('');
	const [openedWorkSpace, { close: closeWorkSpace, open: openWorkSpace }] = useDisclosure(false);
	const [openedEditing, { close: closeEdit, open: openEdit }] = useDisclosure(false);
	const [openedActions, { close: closeActions, open: openActions }] = useDisclosure(false);

	const { data: bookings } = useQuery({
		queryKey: ['bookings'],
		queryFn: bookingApi.getBookingsForClient,
	});

	const filteredBookings = useMemo(() => {
		setActivePage(1);

		switch (currentTab) {
			case 'Все':
				return bookings;

			case 'Активные': {
				return bookings?.filter(
					(item) =>
						dayjs().isBetween(dayjs(item.start), dayjs(item.end)) || isNearestBooking(item.start),
				);
			}

			case 'Будущие': {
				return bookings
					?.filter((item) => isFutureBooking(item.start))
					?.sort((a, b) => dayjs(a.start).unix() - dayjs(b.start).unix());
			}

			case 'Завершенные': {
				return bookings?.filter((item) => isEndedBooking(item.end));
			}
		}
	}, [bookings, currentTab]);

	const hasGuests = useMemo(() => {
		if (currentTab === 'Все' || currentTab === 'Завершенные') return true;

		return filteredBookings?.some((booking) => booking.guests?.some(isActiveGuest)) ?? false;
	}, [filteredBookings, currentTab]);

	const handleFilterBookings = (type: string) => {
		setCurrentTab(type);
	};

	const handleOpenSelectedWorkSpace = (workSpace: PersonBookingsObject) => {
		setSelectedWorkSpace(workSpace);
		openWorkSpace();
	};
	const chunked = chunk(filteredBookings ?? [], 10);

	const rows = chunked[activePage - 1]?.map((booking) => (
		<Table.Tr key={booking.id}>
			<Table.Td>
				<Text size="lg" fw={600} lts="normal">
					{booking.booking_object.name.replace(/\s\|\s[\d\D]+/gm, '')}
				</Text>
				<Text size="md" c="dimmed">
					{booking.booking_object.parent?.parent?.name} • {booking.booking_object.parent?.name}
				</Text>
			</Table.Td>
			<Table.Td>
				<Text size="md" c="dimmed">
					{booking.booking_object.description}
				</Text>
			</Table.Td>
			{hasGuests &&
				(() => {
					const activeGuests = booking.guests?.filter(isActiveGuest) ?? [];

					return (
						<>
							<Table.Td>
								{activeGuests.length ? (
									<Flex direction="column" gap={4}>
										{activeGuests.map((guest) => (
											<Flex key={guest.id ?? guest.full_name} direction="column">
												<Text size="md">{guest.full_name}</Text>
												<Text size="sm" c="dimmed">
													{guest.email}
												</Text>
												<Text size="sm" c="dimmed">
													{guest.phone}
												</Text>
											</Flex>
										))}
									</Flex>
								) : (
									<Text size="md" c="dimmed">
										—
									</Text>
								)}
							</Table.Td>
							<Table.Td>
								{activeGuests.length ? (
									<Flex direction="column" gap={4}>
										{activeGuests.map((guest) => (
											<Text key={guest.id ?? guest.full_name} size="md">
												{guest.renter?.renter_name || '—'}
											</Text>
										))}
									</Flex>
								) : (
									<Text size="md" c="dimmed">
										—
									</Text>
								)}
							</Table.Td>
						</>
					);
				})()}
			<Table.Td>
				<Text size="md" c="dimmed">
					{booking?.car_number}
				</Text>
			</Table.Td>
			<Table.Td>
				<Text size="md" c="dimmed">
					{dayjs(booking.start).format('HH:mm')}
				</Text>
				<Text size="md" c="dimmed">
					{dayjs(booking.start).format('DD MMMM YYYY')}
				</Text>
			</Table.Td>
			<Table.Td>
				<Text size="md" c="dimmed">
					{dayjs(booking.end).format('HH:mm')}
				</Text>
				<Text size="md" c="dimmed">
					{dayjs(booking.end).format('DD MMMM YYYY')}
				</Text>
			</Table.Td>
			{(dayjs().isBetween(dayjs(booking.start), dayjs(booking.end)) ||
				isNearestBooking(booking.start) ||
				isFutureBooking(booking.start)) && (
				<>
					<Table.Td w={40}>
						{booking.booking_object?.is_external ? (
							<CopyButton value={`${window.location.origin}/booking/invite/${booking?.id}`} />
						) : (
							<></>
						)}
					</Table.Td>
					<Table.Td w={40}>
						<IconEdit
							fontSize={20}
							color="gray"
							cursor="pointer"
							onClick={() => {
								navigate(`${AppRoutes.Bookings}/${booking.id}/edit`);
							}}
						/>
					</Table.Td>
					<Table.Td w={40}>
						<IconDelete
							fontSize={20}
							color="gray"
							cursor="pointer"
							onClick={() => {
								setSelectedWorkSpace(booking);
								openActions();
							}}
						/>
					</Table.Td>
				</>
			)}
		</Table.Tr>
	));

	useEffect(() => {
		setCurrentTab('Активные');
	}, []);

	return (
		<>
			{isMobile ? (
				<Flex direction="column" gap={8}>
					<Carousel
						slidesToScroll="auto"
						slideSize="auto"
						slideGap={8}
						align="start"
						bg="#ffffff"
						pos="fixed"
						withControls={false}
						w="100%"
						pb={10}
					>
						{buttonsSlides.map((el) => (
							<Carousel.Slide key={el}>
								<SliderButton active={el !== currentTab} onClick={() => handleFilterBookings(el)}>
									{el}
								</SliderButton>
							</Carousel.Slide>
						))}
					</Carousel>
					<Box mt={54}>
						{filteredBookings &&
							filteredBookings.map((booking) => (
								<LayoutRow key={booking.id} showDivider type="center" style={{ padding: 0 }}>
									<Flex
										justify="space-between"
										onClick={() => handleOpenSelectedWorkSpace(booking)}
									>
										<Flex direction="column" maw={200}>
											<Text size="lg" fw={600} lts="normal">
												{booking.booking_object.name.replace(/\s\|\s[\d\D]+/gm, '')}
											</Text>
											<Text size="md" c="dimmed">
												{booking.booking_object.parent?.parent?.name} •{' '}
												{booking.booking_object.parent?.name}
											</Text>
										</Flex>
										<Flex align="center" gap={5}>
											<Flex direction="column" ta={'right'}>
												<Text c="dimmed" size="md">
													{`С ${dayjs(booking.start).format('HH.mm')} до ${dayjs(booking.end).format('HH.mm')}`}
												</Text>
												<Text c="dimmed" size="md">
													{dayjs(booking.start).format('DD MMMM')}
												</Text>
											</Flex>
											<IconChevronRight
												color="rgba(102, 112, 133, .5)"
												cursor="pointer"
												width={12}
											/>
										</Flex>
									</Flex>
								</LayoutRow>
							))}
					</Box>
				</Flex>
			) : (
				<LayoutRow
					title={{
						title: { children: 'Мои бронирования', size: 'h4', fw: 800 },
					}}
					type="once"
					style={{ height: 'calc(100vh - 116px)' }}
					buttons={[
						{
							children: 'Новое бронирование',
							onClick: () => navigate(AppRoutes.BookingNew),
						},
					]}
				>
					<Flex direction="column" gap={16} style={{ overflow: 'hidden' }}>
						<Carousel
							slidesToScroll="auto"
							slideSize="auto"
							slideGap={8}
							align="start"
							withControls={false}
						>
							{buttonsSlides.map((el) => (
								<Carousel.Slide key={el}>
									<SliderButton active={el !== currentTab} onClick={() => handleFilterBookings(el)}>
										{el}
									</SliderButton>
								</Carousel.Slide>
							))}
						</Carousel>
						{filteredBookings && filteredBookings?.length > 0 ? (
							<>
								<ScrollArea h={'100vh'} scrollbarSize={6} offsetScrollbars>
									<Table verticalSpacing="sm" stickyHeader>
										<Table.Thead>
											<Table.Tr>
												<Table.Th>
													<Text size="md" c="dimmed">
														Название
													</Text>
												</Table.Th>
												<Table.Th>
													<Text size="md" c="dimmed">
														Описание
													</Text>
												</Table.Th>
												{hasGuests && (
													<>
														<Table.Th>
															<Text size="md" c="dimmed">
																Гость
															</Text>
														</Table.Th>
														<Table.Th>
															<Text size="md" c="dimmed">
																Арендатор
															</Text>
														</Table.Th>
													</>
												)}
												<Table.Th>
													<Text size="md" c="dimmed">
														Номер автомобиля
													</Text>
												</Table.Th>
												<Table.Th>
													<Text size="md" c="dimmed">
														Начало
													</Text>
												</Table.Th>
												<Table.Th>
													<Text size="md" c="dimmed">
														Конец
													</Text>
												</Table.Th>
												<Table.Th></Table.Th>
												<Table.Th></Table.Th>
											</Table.Tr>
										</Table.Thead>
										<Table.Tbody>{rows}</Table.Tbody>
									</Table>
								</ScrollArea>
								{chunked.length > 1 && (
									<Pagination
										total={chunked.length}
										value={activePage}
										onChange={setActivePage}
										mt="sm"
									/>
								)}
							</>
						) : (
							<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
								<Text fz={18} fw={700}>
									Пока ничего нет 😔
								</Text>
							</Flex>
						)}
					</Flex>
				</LayoutRow>
			)}

			{selectedWorkSpace && (
				<BottomSheet
					opened={openedWorkSpace}
					onClose={closeWorkSpace}
					title={selectedWorkSpace?.booking_object.description}
					{...(!isEndedBooking(selectedWorkSpace?.end)
						? {
								buttons: [
									{
										children: isActiveBooking(selectedWorkSpace?.start, selectedWorkSpace?.end)
											? 'Завершить'
											: 'Отменить',
										variant: 'default',
										onClick: openActions,
									},
									{
										children: 'Редактировать',
										onClick: openEdit,
									},
								],
							}
						: {
								buttons: [
									{
										children: 'Повторить',
										disabled: true,
									},
								],
							})}
					withBackButton={false}
					isFullScreen
				>
					<ActiveBookingSelected
						booking={selectedWorkSpace}
						actions={{
							type: isActiveBooking(selectedWorkSpace?.start, selectedWorkSpace?.end)
								? 'end'
								: 'cancel',
							opened: openedActions,
							close: closeActions,
							closeParent: closeWorkSpace,
						}}
					/>
				</BottomSheet>
			)}

			{selectedWorkSpace && openedEditing && (
				<EditBooking id={String(selectedWorkSpace.id)} open={openedEditing} close={closeEdit} />
			)}

			{selectedWorkSpace && (
				<ActiveBookingActions
					bookingId={selectedWorkSpace?.id}
					type={
						isActiveBooking(selectedWorkSpace?.start, selectedWorkSpace?.end) ? 'end' : 'cancel'
					}
					opened={openedActions}
					close={closeActions}
				/>
			)}
		</>
	);
};
